import { useEffect, useRef, useState } from "react";
import { Play, Pause, Loader2, GitCompare } from "lucide-react";
import api, { audioUrl } from "@/lib/api";
import SpectrumCurve from "@/components/SpectrumCurve";

const fmt = (s) => (!s || isNaN(s) ? "0:00" : `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`);

// A/B: original (A) vs Mix Bus version (B) — both <audio> run in sync, the toggle just swaps which one is audible.
export default function ABCompare({ gen, kind = "music", testId }) {
  const aRef = useRef(null); const bRef = useRef(null);
  const [side, setSide] = useState("B");
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState({ t: 0, d: 0 });
  const [parent, setParent] = useState(null);
  const mb = gen.mixbus || {};
  const parentId = kind === "music" ? gen.parent_id : gen.mix_of;
  const bSrc = audioUrl(kind === "music" ? gen.audio_files?.mp3 || gen.audio_files?.wav : gen.audio_path);
  const qaB = kind === "music" ? gen.metadata?.qa : gen.qa;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(kind === "music" ? `/generations/${parentId}` : `/voice/${parentId}`);
        let qa = kind === "music" ? data.metadata?.qa : data.qa;
        if (!qa?.spectrum) {
          try { const r = await api.post(kind === "music" ? `/generations/${parentId}/validate` : `/voice/${parentId}/validate`); qa = r.data.qa; } catch { /* keep */ }
        }
        if (alive) setParent({ src: audioUrl(kind === "music" ? data.audio_files?.mp3 || data.audio_files?.wav : data.audio_path), qa, seq: data.seq });
      } catch { if (alive) setParent({ src: mb.parent_audio ? audioUrl(mb.parent_audio) : null, qa: { spectrum: mb.parent_spectrum }, seq: mb.parent_seq }); }
    })();
    return () => { alive = false; };
  }, [parentId, kind, mb.parent_audio, mb.parent_spectrum, mb.parent_seq]);

  useEffect(() => {
    const b = bRef.current; if (!b) return;
    const onTime = () => setProgress({ t: b.currentTime, d: b.duration || 0 });
    const onEnd = () => { setPlaying(false); aRef.current?.pause(); };
    b.addEventListener("timeupdate", onTime); b.addEventListener("loadedmetadata", onTime); b.addEventListener("ended", onEnd);
    return () => { b.removeEventListener("timeupdate", onTime); b.removeEventListener("loadedmetadata", onTime); b.removeEventListener("ended", onEnd); };
  }, [parent]);

  const apply = (s) => { if (aRef.current) aRef.current.muted = s !== "A"; if (bRef.current) bRef.current.muted = s !== "B"; };
  const toggleSide = (s) => {
    setSide(s); apply(s);
    const from = s === "A" ? bRef.current : aRef.current; const to = s === "A" ? aRef.current : bRef.current;
    if (from && to && Math.abs(from.currentTime - to.currentTime) > 0.05) to.currentTime = from.currentTime;
  };
  const togglePlay = async () => {
    const a = aRef.current, b = bRef.current; if (!b) return;
    apply(side);
    if (playing) { a?.pause(); b.pause(); setPlaying(false); return; }
    if (a) { a.currentTime = b.currentTime; await a.play().catch(() => {}); }
    await b.play().catch(() => {}); setPlaying(true);
  };
  const seek = (e) => {
    const b = bRef.current; if (!b?.duration) return;
    const r = e.currentTarget.getBoundingClientRect(); const t = ((e.clientX - r.left) / r.width) * b.duration;
    b.currentTime = t; if (aRef.current) aRef.current.currentTime = t;
  };
  const pct = progress.d ? (progress.t / progress.d) * 100 : 0;

  return (
    <div className="rounded-xl border border-cyan-500/25 bg-cyan-500/[0.04] p-3 space-y-3" data-testid={testId}>
      <audio ref={bRef} src={bSrc} preload="metadata" />
      {parent?.src && <audio ref={aRef} src={parent.src} preload="metadata" muted />}
      <div className="flex items-center gap-3 flex-wrap">
        <button type="button" onClick={togglePlay} disabled={!parent} className="shrink-0 w-10 h-10 rounded-full bg-cyan-500 text-black flex items-center justify-center hover:scale-105 transition-transform" data-testid={`${testId}-play`} aria-label={playing ? "Pause" : "Play"}>
          {!parent ? <Loader2 size={16} className="animate-spin" /> : playing ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
        </button>
        <div className="inline-flex rounded-full border border-cyan-500/40 overflow-hidden text-xs font-mono" role="group" aria-label="A/B">
          <button type="button" onClick={() => toggleSide("A")} className={`px-3 py-1.5 transition-colors ${side === "A" ? "bg-cyan-500 text-black" : "text-cyan-300 hover:bg-cyan-500/10"}`} data-testid={`${testId}-a`}>A · Original{parent?.seq ? ` #${parent.seq}` : ""}</button>
          <button type="button" onClick={() => toggleSide("B")} className={`px-3 py-1.5 transition-colors ${side === "B" ? "bg-cyan-500 text-black" : "text-cyan-300 hover:bg-cyan-500/10"}`} data-testid={`${testId}-b`}>B · {mb.label || "Mix Bus"}</button>
        </div>
        <span className="text-[10px] font-mono text-muted-foreground flex items-center gap-1" data-testid={`${testId}-side`}><GitCompare size={11} /> écoute : {side === "A" ? "original" : "Mix Bus"} · {fmt(progress.t)} / {fmt(progress.d)}</span>
      </div>
      <div className="relative h-2 rounded-full bg-muted cursor-pointer" onClick={seek} data-testid={`${testId}-seek`}>
        <div className="absolute inset-y-0 left-0 rounded-full bg-cyan-400 transition-[width]" style={{ width: `${pct}%` }} />
      </div>
      {qaB?.spectrum && <SpectrumCurve spectrum={qaB.spectrum} compare={parent?.qa?.spectrum} labelA="A · original" labelB="B · Mix Bus" testId={`${testId}-spectrum`} />}
    </div>
  );
}
